import { useEffect, useRef } from "react";

const COLORS = ["0,240,255", "168,85,247", "255,255,255", "57,255,20"];

export default function Particles({ density = 46, className = "" }) {
    const ref = useRef(null);

    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return;
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const ctx = canvas.getContext("2d");
        let raf;
        let w, h, dpr;

        const resize = () => {
            dpr = Math.min(window.devicePixelRatio || 1, 2);
            const rect = canvas.parentElement.getBoundingClientRect();
            w = rect.width;
            h = rect.height;
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            canvas.style.width = `${w}px`;
            canvas.style.height = `${h}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };
        resize();
        window.addEventListener("resize", resize);

        const parts = Array.from({ length: density }, () => ({
            x: Math.random() * w,
            y: Math.random() * h,
            r: 0.6 + Math.random() * 1.8,
            vy: 0.08 + Math.random() * 0.3,
            vx: (Math.random() - 0.5) * 0.12,
            c: COLORS[Math.floor(Math.random() * COLORS.length)],
            tw: Math.random() * Math.PI * 2,
            ts: 0.008 + Math.random() * 0.02,
        }));

        const draw = () => {
            ctx.clearRect(0, 0, w, h);
            for (const p of parts) {
                p.y -= p.vy;
                p.x += p.vx;
                p.tw += p.ts;
                if (p.y < -8) {
                    p.y = h + 8;
                    p.x = Math.random() * w;
                }
                if (p.x < -8) p.x = w + 8;
                if (p.x > w + 8) p.x = -8;
                const a = 0.25 + Math.abs(Math.sin(p.tw)) * 0.5;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(${p.c},${a})`;
                ctx.fill();
            }
            raf = requestAnimationFrame(draw);
        };

        if (reduce) {
            draw();
            cancelAnimationFrame(raf);
            ctx.clearRect(0, 0, w, h);
            for (const p of parts) {
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(${p.c},0.4)`;
                ctx.fill();
            }
        } else {
            raf = requestAnimationFrame(draw);
        }

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", resize);
        };
    }, [density]);

    return <canvas ref={ref} className={`pointer-events-none ${className}`} aria-hidden="true" />;
}
